import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { storeGuestRsvpToken } from '@/lib/guest-rsvp-token';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/layout/Header';
import { formatDate, formatCurrency } from '@/lib/utils';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import { 
    Calendar, 
    MapPin, 
    CheckCircle, 
    XCircle,
    Clock,
    User,
    Mail,
    Award
} from 'lucide-react';
import { RSVP, Event, Course, UnitType } from '@/types';
import { useRanks } from '@/hooks/useRanks';
import { VenueInfo } from '@/components/venues/VenueInfo';
import { getAvailablePrefixes, getRankAndShortName } from '@/lib/rank-utils';
import {
    mergeSingleGuestTicketIntoResponses,
    validateSingleGuestTicketSelections,
} from '@/lib/rsvp-single-guest-ticket';
import {
    getInviterAccessibleTicketsWithFallback,
    parseCommaSeparatedTicketIds,
} from '@/lib/inviter-accessible-tickets';
import { isInviteeCoveredByInviter } from '@/lib/event-utils';
import { BOOKING_FEE_LABEL, getInvoiceCreditRecoveryPounds } from '@/lib/credit-booking-fee';
import { DEFAULT_CREDIT_BOOKING_FEE_PENCE, getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';

export default function InvitationResponsePage() {
    const { unitId, eventId, token } = useParams<{ unitId: string; eventId: string; token: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { ranks } = useRanks();
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [rsvp, setRsvp] = useState<RSVP | null>(null);
    const [event, setEvent] = useState<Event | null>(null);
    const [showDeclineForm, setShowDeclineForm] = useState(false);
    const [showAcceptForm, setShowAcceptForm] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    
    // Form fields for accepting
    const [formData, setFormData] = useState({
        name: '',
        rank: '',
        unit: '',
        email: '',
        specialRequests: '',
    });
    const [courseSelections, setCourseSelections] = useState<Record<string, string>>({});
    const [unitEntityName, setUnitEntityName] = useState('Unit/Organization');
    const [selectedTicketId, setSelectedTicketId] = useState('');
    const [ticketCourseSelections, setTicketCourseSelections] = useState<Record<string, string>>({});
    const [inviterRsvpForScope, setInviterRsvpForScope] = useState<RSVP | null>(null);
    const [currentUnitType, setCurrentUnitType] = useState<UnitType | null>(null);

    const requireAffiliationInfo = event?.requirePrefixRankUnitInfo ?? true;
    const isEventLocked = event?.eventStatus === 'closed';
    const unitLabel = unitEntityName || 'Unit/Organization';
    const availablePrefixes = useMemo(
        () => getAvailablePrefixes(event, currentUnitType ?? undefined),
        [event, currentUnitType],
    );
    const hasConfiguredPrefixes = availablePrefixes.length > 0;

    const availableTickets = useMemo(() => {
        if (!event?.tickets || event.tickets.length === 0) {
            return [];
        }

        const inviterScoped = getInviterAccessibleTicketsWithFallback(
            event.tickets,
            inviterRsvpForScope,
        );

        const filter = rsvp?.ticketFilter?.trim();
        if (filter) {
            const allowedIds = parseCommaSeparatedTicketIds(filter);
            return inviterScoped.filter((ticket) => allowedIds.includes(ticket.id));
        }

        return inviterScoped;
    }, [event?.tickets, rsvp?.ticketFilter, inviterRsvpForScope]);
    const inviterCoversGuest = isInviteeCoveredByInviter(rsvp);

    const creditBookingFeePence = useMemo(() => getCreditBookingFeePenceForEvent(event), [event]);

    const invitationCostHeadline = useMemo(() => {
        if (!event) {
            return { primary: 'N/A', detail: null as string | null };
        }
        if (availableTickets.length === 0) {
            return event.costPerPerson
                ? { primary: formatCurrency(event.costPerPerson), detail: null }
                : { primary: 'N/A', detail: null };
        }
        if (inviterCoversGuest) {
            const minT =
                availableTickets.length === 1
                    ? availableTickets[0].price || 0
                    : Math.min(...availableTickets.map((t) => t.price || 0));
            return {
                primary:
                    availableTickets.length === 1
                        ? formatCurrency(minT)
                        : `From ${formatCurrency(minT)}`,
                detail: null,
            };
        }
        const totals = availableTickets.map((t) => {
            const p = t.price || 0;
            const recovery = getInvoiceCreditRecoveryPounds(event, p, creditBookingFeePence);
            return { p, recovery, total: p + recovery };
        });
        const minCombined = Math.min(...totals.map((x) => x.total));
        if (availableTickets.length === 1) {
            const row = totals[0];
            return {
                primary: formatCurrency(row.total),
                detail:
                    row.recovery > 0
                        ? `Ticket ${formatCurrency(row.p)} plus ${BOOKING_FEE_LABEL} ${formatCurrency(row.recovery)}`
                        : null,
            };
        }
        return {
            primary: `From ${formatCurrency(minCombined)}`,
            detail: totals.some((x) => x.recovery > 0)
                ? `${BOOKING_FEE_LABEL} is set by the lodge for this event when credit cost is passed to guests.`
                : null,
        };
    }, [event, availableTickets, inviterCoversGuest, creditBookingFeePence]);

    useEffect(() => {
        if (!event || availableTickets.length !== 1) {
            return;
        }
        const only = availableTickets[0];
        if (!selectedTicketId || !availableTickets.some((t) => t.id === selectedTicketId)) {
            setSelectedTicketId(only.id);
        }
    }, [event, availableTickets, selectedTicketId]);

    useEffect(() => {
        setTicketCourseSelections({});
    }, [selectedTicketId]);

    useEffect(() => {
        const fetchData = async () => {
            if (!unitId || !eventId || !token) {
                showError('Invalid invitation link');
                setLoading(false);
                return;
            }

            try {
                // Load the invitation via the token-verified callable. RSVPs are no longer publicly
                // readable, so we resolve the booking (and its inviter, for ticket scoping) server-side
                // by presenting the invitation token from the URL.
                const getGuestRsvpFn = httpsCallable(functions, 'getGuestRsvp');
                let guestResult;
                try {
                    guestResult = await getGuestRsvpFn({ unitId, eventId, token });
                } catch (guestErr) {
                    console.error('Error loading invitation:', guestErr);
                    showError('Invitation not found or has expired');
                    setLoading(false);
                    return;
                }

                const payload = guestResult.data as {
                    rsvp: (RSVP & { id: string }) | null;
                    inviter: RSVP | null;
                };
                if (!payload?.rsvp) {
                    showError('Invitation not found or has expired');
                    setLoading(false);
                    return;
                }

                const rsvpData = payload.rsvp as RSVP;

                // Preserve the token for the current session so the dashboard can load this booking.
                storeGuestRsvpToken(rsvpData.id, token);

                // Check if invitation has already been responded to
                if (rsvpData.status !== 'pending') {
                    if (rsvpData.status === 'confirmed') {
                        showSuccess('You have already accepted this invitation');
                        navigate(
                            `/unit/${unitId}/event/${eventId}/rsvp/${rsvpData.id}/dashboard?t=${encodeURIComponent(token)}`,
                        );
                        return;
                    } else if (rsvpData.status === 'declined') {
                        showError('You have already declined this invitation');
                        setLoading(false);
                        return;
                    }
                }

                setRsvp(rsvpData);
                setInviterRsvpForScope(payload.inviter ?? null);

                // Pre-fill form with existing data
                setFormData({
                    name: rsvpData.name || '',
                    rank: rsvpData.rank || '',
                    unit: rsvpData.unit || '',
                    email: rsvpData.email || '',
                    specialRequests: '',
                });

                // Fetch Event
                const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
                if (eventDoc.exists()) {
                    const rawEvent = eventDoc.data();
                    const eventDate = rawEvent.date?.toDate() || new Date();
                    const mappedEvent: Event = {
                        id: eventDoc.id,
                        unitId,
                        name: rawEvent.name,
                        ceremonyType: rawEvent.ceremonyType,
                        date: eventDate,
                        location: rawEvent.location,
                        venueId: rawEvent.venueId,
                        questionnaire: rawEvent.questionnaire || [],
                        courses: rawEvent.courses || [],
                        tickets: rawEvent.tickets || [],
                        maxCapacity: rawEvent.maxCapacity,
                        currentAttendees: rawEvent.currentAttendees || 0,
                        waitlistEnabled: rawEvent.waitlistEnabled || false,
                        waitlist: rawEvent.waitlist || [],
                        rsvpDeadline: rawEvent.rsvpDeadline?.toDate() || new Date(),
                        paymentDeadline: rawEvent.paymentDeadline?.toDate() || eventDate,
                        eventStatus: rawEvent.eventStatus,
                        dresscode: rawEvent.dresscode,
                        customInstructions: rawEvent.customInstructions,
                        isPublic: rawEvent.isPublic ?? false,
                        requirePrefixRankUnitInfo: rawEvent.requirePrefixRankUnitInfo ?? true,
                        useCustomPrefixes: Boolean(rawEvent.useCustomPrefixes),
                        customPrefixes: Array.isArray(rawEvent.customPrefixes)
                            ? rawEvent.customPrefixes.filter(
                                  (p: unknown) => typeof p === 'string' && p.trim() !== '',
                              )
                            : [],
                        createdBy: rawEvent.createdBy,
                        createdAt: rawEvent.createdAt?.toDate() || new Date(),
                        guestCoversCreditCost: rawEvent.guestCoversCreditCost ?? false,
                        creditBookingFeePence:
                            typeof rawEvent.creditBookingFeePence === 'number'
                                ? rawEvent.creditBookingFeePence
                                : undefined,
                        creditCostRecovery:
                            rawEvent.creditCostRecovery &&
                            typeof rawEvent.creditCostRecovery === 'object' &&
                            !Array.isArray(rawEvent.creditCostRecovery)
                                ? {
                                      enabled: Boolean(
                                          (rawEvent.creditCostRecovery as { enabled?: boolean }).enabled,
                                      ),
                                      amountPence:
                                          typeof (rawEvent.creditCostRecovery as { amountPence?: unknown })
                                              .amountPence === 'number'
                                              ? (rawEvent.creditCostRecovery as { amountPence: number }).amountPence
                                              : DEFAULT_CREDIT_BOOKING_FEE_PENCE,
                                  }
                                : { enabled: false, amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE },
                    };
                    setEvent(mappedEvent);

                    // Determine unit entity name for labels
                    let derivedEntityName = 'Unit/Organization';
                    try {
                        const unitSnapshot = await getDoc(doc(db, 'units', unitId));
                        if (unitSnapshot.exists()) {
                            const unitData = unitSnapshot.data();
                            derivedEntityName = unitData.entityName || derivedEntityName;
                            if (unitData.typeId) {
                                try {
                                    const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
                                    if (settingsDoc.exists()) {
                                        const unitTypes = settingsDoc.data().unitTypes || [];
                                        const matchedType = unitTypes.find((type: any) => type.id === unitData.typeId);
                                        if (matchedType?.entityName) {
                                            derivedEntityName = matchedType.entityName;
                                        }
                                        if (matchedType) {
                                            setCurrentUnitType(matchedType as UnitType);
                                        }
                                    }
                                } catch (settingsError) {
                                    console.error('Error fetching unit type metadata:', settingsError);
                                }
                            }
                        }
                    } catch (unitError) {
                        console.error('Error fetching unit metadata:', unitError);
                    }
                    setUnitEntityName(derivedEntityName);

                    // Initialize course selections
                    if (mappedEvent.courses) {
                        const initialSelections: Record<string, string> = {};
                        mappedEvent.courses.forEach(course => {
                            initialSelections[course.id] = '';
                        });
                        setCourseSelections(initialSelections);
                    }
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching invitation data:', error);
                showError('Failed to load invitation information');
                setLoading(false);
            }
        };

        fetchData();
    }, [unitId, eventId, token, navigate]);

    const handleAccept = () => {
        if (isEventLocked) {
            showError('This event is locked. RSVPs are no longer being accepted.');
            return;
        }
        setShowAcceptForm(true);
    };

    const handleSubmitAccept = async () => {
        if (!unitId || !eventId || !rsvp) return;
        if (isEventLocked) {
            showError('This event is locked. RSVPs are no longer being accepted.');
            return;
        }

        // Validate required fields
        if (!formData.name || !formData.email) {
            showError('Please fill in all required fields');
            return;
        }

        if (requireAffiliationInfo && !formData.rank) {
            showError('Please select your prefix.');
            return;
        }
        if (requireAffiliationInfo && !hasConfiguredPrefixes) {
            showError('This unit has no configured prefixes. Please contact the event organizer.');
            return;
        }

        const ticketValidationError = validateSingleGuestTicketSelections(
            availableTickets,
            selectedTicketId,
            ticketCourseSelections,
        );
        if (ticketValidationError) {
            showError(ticketValidationError);
            return;
        }

        // Validate course selections if legacy event-level courses exist (no ticket meal config)
        if (
            availableTickets.length === 0 &&
            event?.courses &&
            event.courses.length > 0
        ) {
            const missingSelections = event.courses.filter((course) => !courseSelections[course.id]);
            if (missingSelections.length > 0) {
                showError('Please select an option for all courses');
                return;
            }
        }

        setProcessing(true);
        try {
            const latestEventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
            if (!latestEventDoc.exists() || latestEventDoc.data()?.eventStatus === 'closed') {
                showError('This event was locked before your RSVP could be submitted.');
                return;
            }

            // Merge with existing responses; match RSVPPage (Allergies + Tickets + TicketCourses)
            const mergedResponses: Record<string, any> = {
                ...(rsvp.responses || {}),
                ...courseSelections,
            };
            mergedResponses.Allergies = formData.specialRequests ?? '';

            mergeSingleGuestTicketIntoResponses(
                mergedResponses,
                availableTickets,
                selectedTicketId,
                ticketCourseSelections,
            );

            const changes: Record<string, any> = {
                name: formData.name,
                email: formData.email,
                specialRequests: formData.specialRequests,
                responses: mergedResponses,
            };

            if (requireAffiliationInfo) {
                changes.rank = formData.rank;
                changes.unit = formData.unit?.trim() || null;

                try {
                    const { rank: resolvedRank, rankShortName } = getRankAndShortName({
                        rankName: formData.rank,
                        currentUnitType: currentUnitType || undefined,
                        globalRanks: ranks,
                    });

                    if (resolvedRank) {
                        changes.rank = resolvedRank;
                    }

                    changes.rankShortName = rankShortName ?? null;
                } catch (error) {
                    console.error('Error deriving rankShortName for invitation response:', error);
                    changes.rankShortName = null;
                }
            }

            // Confirm via the token-verified callable (RSVPs are no longer client-writable by guests).
            // linkToAuthUser lets a logged-in accepter attach the booking to their own account server-side.
            const manageFn = httpsCallable(functions, 'manageGuestRsvp');
            await manageFn({
                unitId,
                eventId,
                rsvpId: rsvp.id,
                token,
                status: 'confirmed',
                changes,
                linkToAuthUser: Boolean(currentUser),
            });

            // If user is logged in, mark the in-app invitation as accepted
            if (currentUser) {
                try {
                    const invitationsQuery = query(
                        collection(db, 'users', currentUser.uid, 'eventInvitations'),
                        where('invitationToken', '==', token)
                    );
                    const invitationsSnapshot = await getDocs(invitationsQuery);
                    
                    if (!invitationsSnapshot.empty) {
                        const invitationDoc = invitationsSnapshot.docs[0];
                        await updateDoc(doc(db, 'users', currentUser.uid, 'eventInvitations', invitationDoc.id), {
                            status: 'accepted',
                            respondedAt: Timestamp.now(),
                        });
                    }
                } catch (error) {
                    console.error('Error updating in-app invitation:', error);
                    // Don't fail the whole operation if this fails
                }
            }

            // Preserve the capability token for this session so the dashboard can load the booking.
            storeGuestRsvpToken(rsvp.id, token || '');

            showSuccess('Invitation accepted! You can now view your RSVP dashboard.');

            // Navigate to RSVP dashboard, preserving ticket filter if present
            let dashboardUrl = `/unit/${unitId}/event/${eventId}/rsvp/${rsvp.id}/dashboard?t=${encodeURIComponent(token || '')}`;
            if (rsvp.ticketFilter) {
                // If there's a ticket filter, redirect to RSVP page with filter instead
                dashboardUrl = `/unit/${unitId}/event/${eventId}/rsvp?tickets=${rsvp.ticketFilter}`;
            }
            navigate(dashboardUrl);
        } catch (error) {
            console.error('Error accepting invitation:', error);
            showError('Failed to accept invitation. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const handleDecline = () => {
        if (isEventLocked) {
            showError('This event is locked. RSVPs are no longer being accepted.');
            return;
        }
        setShowDeclineForm(true);
    };

    const handleSubmitDecline = async () => {
        if (!unitId || !eventId || !rsvp) return;

        const result = await confirmAction({
            title: 'Are you sure you want to decline this invitation?',
            text: 'This action cannot be undone.',
            confirmButtonText: 'Yes, Decline'
        });

        if (!result.isConfirmed) return;

        setProcessing(true);
        try {
            // Decline via the token-verified callable (RSVPs are no longer client-writable by guests).
            const declineFn = httpsCallable(functions, 'manageGuestRsvp');
            await declineFn({
                unitId,
                eventId,
                rsvpId: rsvp.id,
                token,
                status: 'declined',
                changes: { declineReason },
            });

            // If user is logged in, mark the in-app invitation as declined
            if (currentUser) {
                try {
                    const invitationsQuery = query(
                        collection(db, 'users', currentUser.uid, 'eventInvitations'),
                        where('invitationToken', '==', token)
                    );
                    const invitationsSnapshot = await getDocs(invitationsQuery);
                    
                    if (!invitationsSnapshot.empty) {
                        const invitationDoc = invitationsSnapshot.docs[0];
                        await updateDoc(doc(db, 'users', currentUser.uid, 'eventInvitations', invitationDoc.id), {
                            status: 'declined',
                            respondedAt: Timestamp.now(),
                        });
                    }
                } catch (error) {
                    console.error('Error updating in-app invitation:', error);
                    // Don't fail the whole operation if this fails
                }
            }

            showSuccess('Invitation declined. The host has been notified.');
            setLoading(false);
            setShowDeclineForm(false);
        } catch (error) {
            console.error('Error declining invitation:', error);
            showError('Failed to decline invitation. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-center">
                        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                        <p className="text-muted-foreground">Loading invitation...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!rsvp || !event) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-center">
                        <XCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
                        <p className="text-lg font-semibold mb-2">Invitation Not Found</p>
                        <p className="text-muted-foreground">This invitation link is invalid or has expired.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (rsvp.status === 'declined') {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-center">
                        <XCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
                        <p className="text-lg font-semibold mb-2">Invitation Declined</p>
                        <p className="text-muted-foreground">You have declined this invitation.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (showDeclineForm) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <main className="flex-1 px-4 py-8">
                    <div className="container mx-auto max-w-2xl">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <XCircle className="h-5 w-5" />
                                    Decline Invitation
                                </CardTitle>
                                <CardDescription>
                                    We're sorry you can't make it. Please let us know why (optional).
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="declineReason">Reason for Declining (Optional)</Label>
                                    <Textarea
                                        id="declineReason"
                                        value={declineReason}
                                        onChange={(e) => setDeclineReason(e.target.value)}
                                        rows={4}
                                        placeholder="Let the host know why you can't attend..."
                                    />
                                </div>

                                <div className="flex gap-2 justify-end">
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowDeclineForm(false)}
                                        disabled={processing}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        onClick={handleSubmitDecline}
                                        disabled={processing}
                                    >
                                        {processing ? (
                                            <>
                                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                                                Declining...
                                            </>
                                        ) : (
                                            <>
                                                <XCircle className="mr-2 h-4 w-4" />
                                                Decline Invitation
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </main>
            </div>
        );
    }

    if (showAcceptForm) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <main className="flex-1 px-4 py-8">
                    <div className="container mx-auto max-w-3xl space-y-6">
                        <div>
                            <h1 className="text-3xl font-bold">Complete Your RSVP</h1>
                            <p className="text-muted-foreground mt-2">
                                Please provide your information to complete your RSVP for {event.name}
                            </p>
                        </div>

                        <Card>
                            <CardHeader>
                                <CardTitle>Your Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">
                                            Full Name <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email">
                                            Email <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="john@example.com"
                                        />
                                    </div>

                                    {requireAffiliationInfo && (
                                        <>
                                            <div className="space-y-2">
                                                <Label htmlFor="rank">
                                                    Rank <span className="text-red-500">*</span>
                                                </Label>
                                                <select
                                                    id="rank"
                                                    value={formData.rank}
                                                    onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                    required={requireAffiliationInfo}
                                                >
                                                    <option value="">Select your prefix</option>
                                                    {availablePrefixes.map((prefix) => (
                                                        <option key={prefix.id} value={prefix.name}>
                                                            {prefix.shortName
                                                                ? `${prefix.name} (${prefix.shortName})`
                                                                : prefix.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {!hasConfiguredPrefixes && (
                                                    <p className="text-xs text-amber-600">
                                                        No prefixes are configured for this unit type. Please contact the event organizer.
                                                    </p>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="unit">
                                                    {unitLabel}{' '}
                                                    <span className="font-normal text-muted-foreground">(optional)</span>
                                                </Label>
                                                <Input
                                                    id="unit"
                                                    value={formData.unit}
                                                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                                                    placeholder={`Name of your ${unitLabel}`}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="specialRequests">Special Requests (Optional)</Label>
                                    <Textarea
                                        id="specialRequests"
                                        value={formData.specialRequests}
                                        onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                                        rows={3}
                                        placeholder="Any dietary restrictions or special accommodations..."
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {availableTickets.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Ticket &amp; Meal Preferences</CardTitle>
                                    <CardDescription>
                                        Choose your ticket type and meal options for this event
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="invitation-ticket-type">Ticket Type *</Label>
                                        <Select
                                            value={selectedTicketId}
                                            onValueChange={setSelectedTicketId}
                                        >
                                            <SelectTrigger id="invitation-ticket-type">
                                                <SelectValue placeholder="Select a ticket type..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {availableTickets.map((t) => (
                                                    <SelectItem key={t.id} value={t.id}>
                                                        {t.name} - {formatCurrency(t.price || 0)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {selectedTicketId &&
                                        (() => {
                                            const selectedTicket = availableTickets.find(
                                                (t) => t.id === selectedTicketId,
                                            );
                                            if (
                                                !selectedTicket?.courses ||
                                                selectedTicket.courses.length === 0
                                            ) {
                                                return null;
                                            }
                                            return (
                                                <div className="space-y-4">
                                                    <h4 className="text-md font-medium">Meal choices</h4>
                                                    {selectedTicket.courses.map((c) => (
                                                        <div key={c.id} className="space-y-2">
                                                            <Label>{c.name} *</Label>
                                                            <Select
                                                                value={ticketCourseSelections[c.id] || ''}
                                                                onValueChange={(value) => {
                                                                    setTicketCourseSelections({
                                                                        ...ticketCourseSelections,
                                                                        [c.id]: value,
                                                                    });
                                                                }}
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="Select your choice..." />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {(c.options || []).map((option) => (
                                                                        <SelectItem key={option} value={option}>
                                                                            {option}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                </CardContent>
                            </Card>
                        )}

                        {availableTickets.length === 0 &&
                            event.courses &&
                            event.courses.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Course Selections</CardTitle>
                                    <CardDescription>
                                        Please select your preferences for each course
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {event.courses.map((course) => (
                                        <div key={course.id} className="space-y-2">
                                            <Label htmlFor={`course-${course.id}`}>
                                                {course.name} <span className="text-red-500">*</span>
                                            </Label>
                                            <select
                                                id={`course-${course.id}`}
                                                value={courseSelections[course.id] || ''}
                                                onChange={(e) =>
                                                    setCourseSelections({
                                                        ...courseSelections,
                                                        [course.id]: e.target.value,
                                                    })
                                                }
                                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                            >
                                                <option value="">Select an option</option>
                                                {course.options.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        <Card>
                            <CardHeader>
                                <CardTitle>Payment Information</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Cost Per Person</p>
                                        <p className="text-2xl font-bold">
                                            {(() => {
                                                if (availableTickets.length > 0) {
                                                    const t = availableTickets.find(
                                                        (x) => x.id === selectedTicketId,
                                                    );
                                                    if (t && t.price != null) {
                                                        return formatCurrency(t.price);
                                                    }
                                                    return 'Select a ticket';
                                                }
                                                return event.costPerPerson
                                                    ? formatCurrency(event.costPerPerson)
                                                    : 'N/A';
                                            })()}
                                        </p>
                                    </div>
                                    {inviterCoversGuest ? (
                                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                            <p className="text-sm text-blue-800">
                                                <CheckCircle className="inline h-4 w-4 mr-1" />
                                                Your host will be paying for your attendance
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                            <p className="text-sm text-orange-800">
                                                <Clock className="inline h-4 w-4 mr-1" />
                                                You will need to make payment after confirming your RSVP
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <div className="flex gap-2 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => setShowAcceptForm(false)}
                                disabled={processing}
                            >
                                Back
                            </Button>
                            <Button
                                onClick={handleSubmitAccept}
                                disabled={processing}
                            >
                                {processing ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                                        Confirming...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="mr-2 h-4 w-4" />
                                        Confirm RSVP
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // Initial invitation view
    return (
        <div className="flex min-h-screen flex-col">
            <Header />
            <main className="flex-1 px-4 py-8">
                <div className="container mx-auto max-w-3xl space-y-6">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold mb-2">You're Invited!</h1>
                        <p className="text-lg text-muted-foreground">
                            You have been invited to attend {event.name}
                        </p>
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-2xl">{event.name}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-start gap-3">
                                    <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold">Date & Time</p>
                                        <p className="text-sm">{formatDate(event.date)}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold">Location</p>
                                        <div className="text-sm">
                                            <VenueInfo
                                                unitId={unitId!}
                                                venueId={event.venueId}
                                                location={event.location}
                                                compact={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t">
                                <p className="text-sm font-semibold mb-2">Cost Per Person</p>
                                <p className="text-2xl font-bold">{invitationCostHeadline.primary}</p>
                                {invitationCostHeadline.detail ? (
                                    <p className="text-sm text-muted-foreground mt-2">{invitationCostHeadline.detail}</p>
                                ) : null}
                                {inviterCoversGuest && (
                                    <p className="text-sm text-blue-600 mt-2">
                                        <CheckCircle className="inline h-4 w-4 mr-1" />
                                        Your host will be paying for you
                                    </p>
                                )}
                            </div>

                            {availableTickets.length > 0 && (
                                <div className="pt-4 border-t">
                                    <p className="text-sm font-semibold mb-3">Tickets &amp; meal options</p>
                                    <div className="space-y-3">
                                        {availableTickets.map((ticket) => (
                                            <div key={ticket.id} className="text-sm rounded-md border p-3">
                                                <p className="font-medium">
                                                    {ticket.name}{' '}
                                                    <span className="text-muted-foreground">
                                                        ({formatCurrency(ticket.price || 0)})
                                                    </span>
                                                </p>
                                                {ticket.courses && ticket.courses.length > 0 && (
                                                    <ul className="mt-2 space-y-1 text-muted-foreground">
                                                        {ticket.courses.map((course) => (
                                                            <li key={course.id}>
                                                                <span className="font-medium text-foreground">
                                                                    {course.name}
                                                                </span>
                                                                : {course.options.join(', ')}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        You will choose your ticket and meals when you accept the invitation.
                                    </p>
                                </div>
                            )}

                            {event.dresscode && (
                                <div className="pt-4 border-t">
                                    <p className="text-sm font-semibold mb-2">Dress Code</p>
                                    <p className="text-sm">{event.dresscode}</p>
                                </div>
                            )}

                            {event.customInstructions && (
                                <div className="pt-4 border-t">
                                    <p className="text-sm font-semibold mb-2">Additional Information</p>
                                    <p className="text-sm">{event.customInstructions}</p>
                                </div>
                            )}

                            {availableTickets.length === 0 &&
                                event.courses &&
                                event.courses.length > 0 && (
                                <div className="pt-4 border-t">
                                    <p className="text-sm font-semibold mb-3">Menu Courses</p>
                                    <div className="space-y-2">
                                        {event.courses.map((course) => (
                                            <div key={course.id} className="text-sm">
                                                <p className="font-medium">{course.name}</p>
                                                <p className="text-muted-foreground">
                                                    {course.options.join(', ')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {isEventLocked && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                    <p className="text-sm text-amber-800">
                                        This event is locked and no further invitation responses are being accepted.
                                    </p>
                                </div>
                            )}

                            <div className="flex gap-3 pt-6 border-t">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={handleDecline}
                                    disabled={processing || isEventLocked}
                                >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Decline
                                </Button>
                                <Button
                                    className="flex-1"
                                    onClick={handleAccept}
                                    disabled={processing || isEventLocked}
                                >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Accept & RSVP
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
}

